import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Exam, Medal } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTranslation } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useQuizBadges } from "@/contexts/QuizBadgesContext";
import { getLessonQuiz } from "@/data/quizzes";
import { LEARNING_PATH } from "@/data/learningPath";
import LessonQuiz from "@/components/quiz/LessonQuiz";
import PrintQuizButton from "@/components/quiz/PrintQuizButton";

// Auto-rendered quiz at the bottom of every lesson that has one.
export default function LessonQuizSection() {
  const { pathname: rawPath, hash } = useLocation();
  const pathname = rawPath.replace(/\/+$/, "") || "/";
  const { t, lang } = useTranslation();
  const { user } = useAuth();
  const { badges, markPerfect } = useQuizBadges();
  const questions = getLessonQuiz(pathname, lang);
  const ready = !!questions && !!user;

  useEffect(() => {
    if (hash !== "#lesson-quiz" || !ready) return;
    const id = setTimeout(() => document.getElementById("lesson-quiz")?.scrollIntoView({ behavior: "smooth" }), 400);
    return () => clearTimeout(id);
  }, [hash, pathname, ready]);

  if (!ready) return null;
  const es = lang === "es";
  const lesson = LEARNING_PATH.find((l) => l.to === pathname);
  const title = lesson ? t(lesson.labelKey) : "";
  const earned = badges.has(pathname);

  const onFinish = async (score, total) => {
    if (score === total && (await markPerfect(pathname))) {
      toast.success(es ? "¡Insignia ganada! 5/5 en esta lección" : "Badge earned! 5/5 on this lesson");
    }
  };

  return (
    <section id="lesson-quiz" className="print:hidden max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-4 scroll-mt-20" data-testid="lesson-quiz-section">
      <div className="border-t border-white/10 pt-8">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div>
            <p className="flex items-center gap-1.5 text-[#F5A524] text-xs uppercase tracking-[0.2em] mb-1">
              <Exam weight="fill" className="w-4 h-4" />
              {es ? "Cuestionario de la lección" : "Lesson quiz"}
            </p>
            <h2 className="font-['Outfit'] text-white text-xl font-semibold mb-1">
              {es ? "Pon a prueba lo aprendido" : "Check what you learned"}
            </h2>
            <p className="text-[#71717A] text-sm">
              {es ? `${questions.length} preguntas · verás la explicación después de cada respuesta.` : `${questions.length} questions · you'll see an explanation after each answer.`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {earned && (
              <span className="flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/30" data-testid="lesson-quiz-badge">
                <Medal weight="fill" className="w-3.5 h-3.5" />
                {es ? "Insignia ganada" : "Badge earned"}
              </span>
            )}
            <PrintQuizButton path={pathname} title={title} testId="lesson-quiz-print" />
          </div>
        </div>
        <LessonQuiz key={`${pathname}-${lang}`} questions={questions} onFinish={onFinish} />
      </div>
    </section>
  );
}
